import { TemplateVariableBindingSummary } from "./template-variable-binding-summary";
import { getVariableEditPolicy } from "@/domains/templates/step-variable-bindings";
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import type { TemplateInputFieldEditorData } from "@/domains/templates/components/template-input-fields/template-input-field-editor-row";
import { templateQueries } from "@/domains/templates/queries";
import type {
  TemplateVariableTarget,
  Variable,
} from "@/domains/templates/types";
import { getErrorMessage } from "@/shared/lib/error-message";
import { cn } from "@/shared/lib/utils";
import { useTemplateVariableMutations } from "../hooks/use-template-variable-mutations";
import { formatVariableDefault } from "../lib/format-variable-default";
import { createVariableReferenceResolver } from "../lib/resolve-variable-name";
import { TemplateVariableDeleteDialog } from "./template-variable-delete-dialog";
import { TemplateVariableDialog } from "./template-variable-dialog";

type TemplateVariableListProps = {
  target: TemplateVariableTarget;
  group?: "input" | "output";
  variables: Variable[];
  isReadOnly?: boolean;
  emptyText?: string;
  editorData: TemplateInputFieldEditorData;
};

type VariableSourceLevel = "Global" | "Tenant" | "Analysis";

const sourceLevelOrder = {
  Global: 0,
  Tenant: 1,
  Analysis: 2,
  Custom: 3,
} as const;

export function TemplateVariableList({
  target,
  group,
  variables: allVariables,
  isReadOnly = false,
  emptyText = "No variables configured.",
  editorData,
}: TemplateVariableListProps) {
  const variables = allVariables;
  const [editingVariable, setEditingVariable] = useState<Variable>();
  const [variableDialogOpen, setVariableDialogOpen] = useState(false);
  const [deletingVariable, setDeletingVariable] = useState<Variable>();
  const [deleteError, setDeleteError] = useState<string>();
  const currentTenantId = editorData.currentTenantId;
  const queryClient = useQueryClient();
  const mutations = useTemplateVariableMutations(target);
  const catalogGroups = editorData.catalogVariableGroups;
  const analysisVariables = editorData.analysisVariables;
  const resolveReference = useMemo(
    () =>
      createVariableReferenceResolver([
        ...variables,
        ...analysisVariables,
        ...catalogGroups.flatMap((group) => group.variables),
      ]),
    [analysisVariables, catalogGroups, variables],
  );
  const selectableVariables = useMemo(
    () =>
      target.level === "analysis"
        ? catalogGroups.flatMap((group) => group.variables)
        : [
            ...analysisVariables.filter(
              (variable) =>
                variable.parent_variable_id === null ||
                ["subanalysis_source", "bound_input"].includes(
                  variable.variable_role,
                ),
            ),
            ...catalogGroups
              .flatMap((group) => group.variables)
              .filter(
                (variable) =>
                  variable.record_tenant_id === currentTenantId ||
                  variable.variable_kind === "tool",
              ),
          ],
    [analysisVariables, catalogGroups, currentTenantId, target.level],
  );
  const rootVariables = variables.filter(
    (variable) =>
      variable.parent_variable_id === null ||
      ["subanalysis_source", "bound_input"].includes(variable.variable_role),
  );
  const variableRows = rootVariables
    .map((variable, originalIndex) => {
      const { name: variableName, directSource } = resolveReference(variable);
      const sourceLevel: VariableSourceLevel | undefined = !directSource
        ? undefined
        : target.level === "analysisstep" &&
            analysisVariables.some(
              (item) => item.variable_id === directSource.variable_id,
            )
          ? "Analysis"
          : directSource.record_tenant_id === currentTenantId
            ? "Tenant"
            : "Global";

      return { variable, variableName, sourceLevel, originalIndex };
    })
    .sort(
      (left, right) =>
        sourceLevelOrder[left.sourceLevel ?? "Custom"] -
          sourceLevelOrder[right.sourceLevel ?? "Custom"] ||
        left.originalIndex - right.originalIndex,
    );

  const handleDelete = async () => {
    if (!deletingVariable) return;
    setDeleteError(undefined);
    try {
      await mutations.deleteVariable.mutateAsync({
        variableId: deletingVariable.variable_id,
      });
      setDeletingVariable(undefined);
    } catch (error) {
      setDeleteError(getErrorMessage(error, "Could not remove variable"));
    }
  };

  const openAddDialog = () => {
    setEditingVariable(undefined);
    setVariableDialogOpen(true);
  };

  const addVariableButton = !isReadOnly ? (
    <Button type="button" variant="outline" size="sm" onClick={openAddDialog}>
      Add variable
    </Button>
  ) : null;

  return (
    <div className="grid gap-1.5 rounded-md border bg-muted/20 p-2">
      {rootVariables.length === 0 ? (
        <div
          data-testid="template-variable-empty-row"
          className="flex min-h-8 items-center justify-between gap-3"
        >
          <p className="text-sm text-muted-foreground">{emptyText}</p>
          {addVariableButton}
        </div>
      ) : (
        variableRows.map(({ variable, variableName, sourceLevel }) => {
          return (
            <div
              key={variable.variable_id}
              data-testid={`template-variable-row-${variable.variable_id}`}
              className="flex min-h-9 items-center justify-between gap-3 rounded-md border bg-background px-2 py-1"
            >
              <div className="flex min-w-0 items-center gap-2">
                <div
                  data-testid={`template-variable-source-${variable.variable_id}`}
                  className="flex w-16 shrink-0 items-center"
                >
                  {sourceLevel ? (
                    <Badge
                      className={cn(
                        "h-5 px-1.5 text-[10px]",
                        sourceLevel === "Tenant" && "bg-primary/70",
                        sourceLevel === "Analysis" &&
                          "bg-primary/20 text-primary",
                      )}
                    >
                      {sourceLevel}
                    </Badge>
                  ) : null}
                </div>
                <p
                  data-testid={`template-variable-name-${variable.variable_id}`}
                  className="truncate text-sm font-medium"
                >
                  {variableName}
                </p>
                {target.level === "analysisstep" && variable.binding_kind && (
                  <TemplateVariableBindingSummary
                    variable={variable}
                    templateId={target.templateId}
                    stepId={target.stepId}
                    parentVariables={analysisVariables}
                  />
                )}
                {!variable.binding_kind &&
                  variable.variable_default !== null &&
                  variable.variable_default !== undefined && (
                    <span className="truncate text-xs text-muted-foreground">
                      {`${variable.variable_type.charAt(0).toUpperCase()}${variable.variable_type.slice(1)}: ${formatVariableDefault(variable.variable_default)}`}
                    </span>
                  )}
              </div>
              {!isReadOnly && (
                <div className="flex gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Edit ${variableName}`}
                    onClick={() => {
                      setEditingVariable(variable);
                      setVariableDialogOpen(true);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {getVariableEditPolicy(variable).canDelete &&
                    variable.variable_role !== "bound_input" && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Remove ${variableName}`}
                        onClick={() => {
                          setDeleteError(undefined);
                          setDeletingVariable(variable);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                </div>
              )}
            </div>
          );
        })
      )}
      {rootVariables.length > 0 && !isReadOnly && (
        <div className="flex justify-end">{addVariableButton}</div>
      )}
      <TemplateVariableDialog
        open={variableDialogOpen}
        target={target}
        group={group}
        variable={editingVariable}
        variables={variables}
        selectableVariables={selectableVariables}
        editorData={editorData}
        loadChildren={(sourceVariableId) =>
          target.level === "analysisstep" &&
          analysisVariables.some(
            (variable) => variable.variable_id === sourceVariableId,
          )
            ? Promise.resolve(
                analysisVariables.filter(
                  (variable) =>
                    variable.parent_variable_id === sourceVariableId,
                ),
              )
            : queryClient.fetchQuery(
                templateQueries.variableChildren(
                  sourceVariableId,
                  true,
                  currentTenantId,
                ),
              )
        }
        onOpenChange={(open) => {
          setVariableDialogOpen(open);
          if (!open) setEditingVariable(undefined);
        }}
      />
      <TemplateVariableDeleteDialog
        open={Boolean(deletingVariable)}
        name={
          deletingVariable
            ? resolveReference(deletingVariable).name
            : "variable"
        }
        isPending={mutations.deleteVariable.isPending}
        error={deleteError}
        onOpenChange={(open) => {
          if (!open) setDeletingVariable(undefined);
        }}
        onConfirm={() => void handleDelete()}
      />
    </div>
  );
}
